import React, { createContext, useState, useEffect, useContext } from 'react';
import commonApi from '../utils/api';
import { CommonContext } from './CommonContext';
import { format } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';

export const CreditNoteContext = createContext();

const initialFilters = {
    searchBy: 'credit_note_number',
    searchValue: '',
    merchant_id: [],
    dateRange: {
        startDate: moment().subtract(1, 'month').toDate(),
        endDate: moment().add(0, 'month').toDate(),
    }
};

export function CreditNoteContextProvider({ children }) {
    const { user, setModalAlert, setOpenAlert, setAlertMessage, setSeverity } = useContext(CommonContext);

    const [addModal, setAddModal] = useState(false);
    const [allData, setAllData] = useState([]);
    const [allInvoice, setAllInvoice] = useState([]);
    const [viewData, setViewData] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [isLoading, setIsLoading] = useState(false);
    const [firstLoading, setFirstLoading] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selected, setSelected] = useState([]);
    const [filters, setFilters] = useState(initialFilters);
    const [locationState, setLocationState] = useState(null);
    const [userGst, setUserGst] = useState([]);

    const location = useLocation();
    const navigate = useNavigate();

    const handleAddModal = () => {
        setAddModal(true);
        setModalAlert(false);
        setOpenAlert(false);
        setAlertMessage('');
        setSeverity('');
        let postReq = {}
        if (user?.user_type === "Merchant") {
            postReq.merchant_id = user?.id
        } else {
            postReq.admin_id = user?.id
        }
        setIsLoading(true);
        commonApi.post('/credit-note/user-gst', postReq)
            .then((response) => {
                setIsLoading(false);
                if (response?.data?.data) {
                    setUserGst(response?.data?.data);
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error('Error fetching data:', error);
            });
    };

    const handleCloseModal = () => {
        setViewData('')
        setIsEdit(false);
        setAddModal(false);
    };

    const onLoadApi = (customFilters = filters) => {
        let payload = {
            ...customFilters,
            start_date: customFilters?.dateRange?.startDate
                ? format(new Date(customFilters.dateRange.startDate), 'yyyy-MM-dd')
                : null,
            end_date: customFilters?.dateRange?.endDate
                ? format(new Date(customFilters.dateRange.endDate), 'yyyy-MM-dd')
                : null,
        };

        if (user?.user_type === "Merchant") {
            payload.merchant_id = user?.id;
        } else if (location?.state) {
            payload.merchant_id = location.state.id;
            setLocationState(location.state);
        } else {
            payload.admin_id = user?.id;
        }

        delete payload.dateRange;
        setFirstLoading(false);
        commonApi.post(`/credit-note/list?page=${page + 1}&per_page=${rowsPerPage}`, payload)
            .then((response) => {
                setFirstLoading(true);
                if (response?.data?.data) {
                    setAllInvoice(response?.data?.data);
                    setAllData(response?.data);
                }
            })
            .catch((error) => {
                setFirstLoading(true);
                console.error('Error fetching data:', error);
            });
    };

    const viewDetail = (id) => {
        setIsLoading(true);
        commonApi.post('/credit-note/view', { id: id })
            .then((response) => {
                setIsLoading(false);
                if (response?.data?.data) {
                    setIsEdit(true);
                    setViewData(response?.data?.data);
                    handleAddModal();
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error('Error fetching data:', error);
            });
    }

    const onExport = (id) => {
        let payload = {};
        if (id) {
            payload = { invoice_id: id };
        } else {
            payload = {
                ...filters,
                start_date: filters?.dateRange?.startDate
                    ? format(new Date(filters.dateRange.startDate), "yyyy-MM-dd")
                    : null,
                end_date: filters?.dateRange?.endDate
                    ? format(new Date(filters.dateRange.endDate), "yyyy-MM-dd")
                    : null,
            };
            delete payload.dateRange;
        }

        if (user?.user_type !== "Merchant") {
            payload.admin_id = user?.id;
        } else {
            payload.merchant_id = user?.id;
        }
        setIsLoading(true);
        commonApi.post('/credit-note/export', payload)
            .then((response) => {
                setIsLoading(false);
                setOpenAlert(true);
                if (response?.data?.s === 0) {
                    setAlertMessage(response?.data?.msg);
                    setSeverity('error')
                } else {
                    navigate('/download');
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error('Error fetching data:', error);
                setOpenAlert(true);
                setAlertMessage(error?.message || 'Something went wrong');
                setSeverity('error')
            });
    };
    const onExportPDF = (id) => {
        let payload = {};
        if (id) {
            payload = { id: id };
        }
        setIsLoading(true);
        commonApi.post('/credit-note/generate-invoice-pdf', payload)
            .then((response) => {
                setIsLoading(false);
                setOpenAlert(true);
                if (response?.data?.s === 0) {
                    setAlertMessage(response?.data?.msg);
                    setSeverity('error')
                } else {
                    navigate('/download');
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error('Error fetching data:', error);
                setOpenAlert(true);
                setAlertMessage(error?.message || 'Something went wrong');
                setSeverity('error')
            });
    };
    useEffect(() => {
        onLoadApi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    // You can provide any values or functions you want to make available to consumers here
    const contextValue = {
        onExportPDF,
        onExport,
        userGst,
        setUserGst,
        locationState,
        setLocationState,
        filters,
        setFilters,
        initialFilters,
        viewDetail,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
        selected,
        setSelected,
        isEdit,
        onLoadApi,
        isLoading,
        firstLoading,
        viewData,
        allInvoice,
        allData,
        handleAddModal,
        handleCloseModal,
        setAddModal,
        addModal,
        setAllData,
        setAllInvoice,
        setViewData,
        setFirstLoading,
        setIsLoading,
        setIsEdit,
    };

    // Render the provider with children
    return <CreditNoteContext.Provider value={contextValue}>{children}</CreditNoteContext.Provider>;
}
